/**
 * File Service
 * Centralized API calls for file upload/download operations
 */

/**
 * Get presigned upload URL
 */
export async function getUploadUrl(params) {
    const {
        formId,
        applicationId,
        fieldId,
        filename,
        contentType,
        contentLength
    } = params;

    const response = await fetch('/api/files/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            formId,
            applicationId,
            fieldId,
            filename,
            contentType,
            contentLength
        })
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get upload URL');
    }

    const result = await response.json();
    return result.data;
}

/**
 * Complete file upload (record attachment metadata)
 */
export async function completeUpload(params) {
    const {
        applicationId,
        fieldId,
        s3Key,
        filename,
        contentType,
        contentLength
    } = params;

    const response = await fetch('/api/files/upload-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            applicationId,
            fieldId,
            s3Key,
            filename,
            contentType,
            contentLength
        })
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to complete upload');
    }

    const result = await response.json();
    return result.data;
}

/**
 * Get presigned download URL
 */
export async function getDownloadUrl(applicationId, attachmentIndex) {
    const response = await fetch(
        `/api/files/${applicationId}/download?attachmentIndex=${attachmentIndex}`,
        { credentials: 'include' }
    );

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get download URL');
    }

    const result = await response.json();
    return result.data;
}

/**
 * Delete attachment
 */
export async function deleteAttachment(applicationId, attachmentIndex) {
    const response = await fetch(
        `/api/files/${applicationId}/${attachmentIndex}`,
        {
            method: 'DELETE',
            credentials: 'include'
        }
    );

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete attachment');
    }

    const result = await response.json();
    return result.data;
}

/**
 * Get file service status and configuration
 */
export async function getFileServiceStatus() {
    const response = await fetch('/api/files/status', {
        credentials: 'include'
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get file service status');
    }

    const result = await response.json();
    return result.data;
}

/**
 * Upload file to S3 using presigned URL with progress tracking
 * 
 * @param {File} file - File object
 * @param {string} presignedUrl - Presigned URL from backend
 * @param {Function} onProgress - Callback with progress percentage
 * @returns {Promise<{statusCode: number}>}
 */
export function uploadFileToS3(file, presignedUrl, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100;
                if (onProgress) {
                    onProgress(percentComplete);
                }
            }
        });

        xhr.addEventListener('load', () => {
            // Check for HTTP errors (4xx, 5xx)
            if (xhr.status >= 400) {
                const errorMessage = `S3 Upload failed with status ${xhr.status} ${xhr.statusText}`;
                const details = xhr.responseText ? `: ${xhr.responseText}` : '';
                console.error('[FileService] Upload error:', errorMessage + details);
                reject(new Error(errorMessage + details));
                return;
            }
            
            resolve({
                statusCode: xhr.status,
                statusText: xhr.statusText
            });
        });

        xhr.addEventListener('error', () => {
            const errorMsg = `Network error uploading to S3. Status: ${xhr.status}, Response: ${xhr.responseText || 'No response'}`;
            console.error('[FileService] XHR Error:', errorMsg);
            reject(new Error(errorMsg));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload cancelled by user'));
        });

        // Handle timeout
        xhr.timeout = 60000; // 60 seconds
        xhr.addEventListener('timeout', () => {
            reject(new Error('Upload timeout - file upload took too long'));
        });

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        
        // Log upload attempt
        console.debug('[FileService] Starting S3 upload:', {
            filename: file.name,
            size: file.size,
            type: file.type,
            urlPreview: presignedUrl.substring(0, 100) + '...'
        });
        
        xhr.send(file);
    });
}

/**
 * Validate file before upload
 * 
 * @param {File} file - File object
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Max size in MB
 * @param {string} options.accept - Accepted file types (comma-separated)
 * @returns {Object} {valid: boolean, error: string|null}
 */
export function validateFile(file, options = {}) {
    const { maxSize = 50, accept = '' } = options;

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
        return {
            valid: false,
            error: `File exceeds maximum size of ${maxSize}MB`
        };
    }

    // Check file type
    if (accept) {
        const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
        const fileExt = '.' + file.name.split('.').pop().toLowerCase();
        const fileMimeType = file.type.toLowerCase();

        const isAccepted = acceptedTypes.some(type => {
            if (type.startsWith('.')) {
                return fileExt === type;
            }
            return fileMimeType === type || fileMimeType.startsWith(type);
        });

        if (!isAccepted) {
            return {
                valid: false,
                error: `File type not allowed: ${fileExt}`
            };
        }
    }

    return { valid: true, error: null };
}

export default {
    getUploadUrl,
    completeUpload,
    getDownloadUrl,
    deleteAttachment,
    getFileServiceStatus,
    uploadFileToS3,
    validateFile
};
